export type QuickOrderItemInput = {
  category?: string;
  name: string;
  note?: string;
  quantity: number;
  unit: string;
  unitPrice: number;
  supplier: string;
};

export type QuickQuoteRequestInput = {
  product: string;
  quantity: number;
  unit: string;
  neededBy: string;
  supplierName: string;
};

export type QuickInvoiceInput = {
  supplierName: string;
  amountBam: number;
  orderId: string;
  due: string;
  fileUrl?: string;
  lineItems?: QuickOrderItemInput[];
  lineText?: string;
};

export function formatMoney(amount: number) {
  return new Intl.NumberFormat("en-BA", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

export function parseQuickDecimal(value?: string) {
  if (!value) {
    return 0;
  }

  const normalized = normalizeQuickToken(value);
  const numberWords: Record<string, number> = {
    jedan: 1,
    jedna: 1,
    jedno: 1,
    jednu: 1,
    dva: 2,
    dvije: 2,
    dve: 2,
    tri: 3,
    cetiri: 4,
    pet: 5,
    sest: 6,
    sedam: 7,
    osam: 8,
    devet: 9,
    deset: 10,
  };

  if (numberWords[normalized]) {
    return numberWords[normalized];
  }

  const parsed = Number(normalized.replace(",", "."));
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

function normalizeQuickToken(value?: string) {
  return (value ?? "")
    .trim()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/\.$/, "");
}

export function normalizeQuickUnit(unit?: string) {
  const normalized = normalizeQuickToken(unit);

  if (["l", "ltr", "liter", "liters", "litar", "litara", "litra"].includes(normalized)) {
    return "L";
  }

  if (["kom", "komad", "komada", "pc", "pcs", "piece", "pieces"].includes(normalized)) {
    return "pcs";
  }

  if (["case", "cases"].includes(normalized)) {
    return "case";
  }

  if (["box", "boxes", "kutija", "kutije"].includes(normalized)) {
    return "box";
  }

  if (["pack", "packs", "pak", "paket", "paketa"].includes(normalized)) {
    return "pack";
  }

  if (["kilogram", "kilograma", "kila", "kilo"].includes(normalized)) {
    return "kg";
  }

  if (["gr", "gram", "grama"].includes(normalized)) {
    return "g";
  }

  if (["karton", "kartona"].includes(normalized)) {
    return "karton";
  }

  if (["gajba", "gajbe", "gajbi"].includes(normalized)) {
    return "gajba";
  }

  if (["vreca", "vrece", "kesa", "kese", "vrecica", "vrecice"].includes(normalized)) {
    return "bag";
  }

  if (["boca", "boce", "flasa", "flase"].includes(normalized)) {
    return "bottle";
  }

  if (["kanister", "kanistera"].includes(normalized)) {
    return "canister";
  }

  if (["vezica", "vezice"].includes(normalized)) {
    return "bunch";
  }

  if (["bala", "bale"].includes(normalized)) {
    return "bale";
  }

  if (["glava", "glave", "glavica", "glavice"].includes(normalized)) {
    return "head";
  }

  if (["kanta", "kante", "kantu", "kanti"].includes(normalized)) {
    return "bucket";
  }

  if (["rola", "role"].includes(normalized)) {
    return "roll";
  }

  return normalized || "unit";
}

const quickQuantityPattern =
  "\\d+(?:[,.]\\d+)?|jedan|jedna|jedno|jednu|dva|dvije|dve|tri|cetiri|\\u010detiri|pet|sest|\\u0161est|sedam|osam|devet|deset";
const quickUnitPattern =
  "kg|kgs|kilogram|kilograma|kila|kilo|g|gr|gram|grama|l|ltr|liter|liters|litar|litara|litra|kom|komad|komada|pcs|pc|pieces|piece|pak|paket|paketa|pack|packs|box|boxes|kutija|kutije|case|cases|karton|kartona|gajba|gajbe|gajbi|vreca|vrece|vre\\u0107e|vrecica|vre\\u0107ica|vrecice|vre\\u0107ice|kesa|kese|boca|boce|flasa|fla\\u0161a|flase|fla\\u0161e|kanister|kanistera|vezica|vezice|bala|bale|glava|glave|glavica|glavice|kanta|kante|kantu|kanti|rola|role";
const quantityUnitRegex = new RegExp(
  `(?:^|\\s)(${quickQuantityPattern})\\s*(${quickUnitPattern})\\b`,
  "i",
);
const reversedQuantityUnitRegex = new RegExp(
  `(?:^|\\s)(${quickUnitPattern})\\s*(${quickQuantityPattern})\\b`,
  "i",
);
const unitPriceRegex = new RegExp(
  `\\b(\\d+(?:[,.]\\d+)?)\\s*(?:km|bam)?\\s*/\\s*(?:${quickUnitPattern})\\b`,
  "i",
);
const multiplierWithUnitRegex = new RegExp(
  `(?:^|\\s)(?:x\\s*(${quickQuantityPattern})|(${quickQuantityPattern})\\s*x)\\s*(${quickUnitPattern})\\b`,
  "i",
);
const leadingUnitAfterMultiplierRegex = new RegExp(`^(${quickUnitPattern})\\s+(.+)`, "i");

function cleanQuickLine(value: string) {
  return value
    .trim()
    .replace(/^[-*]+\s*/, "")
    .replace(/^\d+[.)]\s+/, "")
    .replace(/[;|]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function stripTextRange(value: string, match: RegExpMatchArray) {
  const index = match.index ?? 0;
  return `${value.slice(0, index)} ${value.slice(index + match[0].length)}`.replace(/\s+/g, " ").trim();
}

function cleanQuickProductName(value: string) {
  return value
    .replace(/^[,.:/\\\-–—]+/, "")
    .replace(/[,.:/\\\-–—]+$/, "")
    .replace(/\s+/g, " ")
    .trim();
}

function cleanQuickSupplierName(value: string) {
  return value
    .replace(/^\s*(?:supplier|dobavljac|dobavlja\u010d)\s*[:=-]?\s+/i, "")
    .replace(/^[,.:/\\\-–—]+/, "")
    .replace(/[,.:/\\\-–—]+$/, "")
    .replace(/\s+/g, " ")
    .trim();
}

function stripQuickOrderChatter(value: string) {
  return value
    .replace(/\bnadam\s+se\s+da\s+si\s+dobro\b/gi, " ")
    .replace(/\b(?:selam|ahbab|pozdrav|dobar\s+dan)\b[,\s]*/gi, " ")
    .replace(/\bako\s+nije\s+kasno\b/gi, " ")
    .replace(/\bdodati\s+i\b/gi, " ")
    .replace(/\bbilo\s+bi\s+odlicno\b/gi, " ")
    .replace(/\bako\s+moze\b/gi, " ")
    .replace(/\bmoze\b/gi, " ")
    .replace(/\bza\s+sutra\b/gi, " ")
    .replace(/\b(?:sutra|danas|samo)\b/gi, " ")
    .replace(/\bmolim\s+vas\b/gi, " ")
    .replace(/\bhvala(?:\s+(?:puno|lijepa|lijepo|ljepa|ljepo|lepa|lepo))?\b/gi, " ")
    .replace(/:+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function isQuickChatterLine(value: string) {
  const normalized = normalizeQuickToken(value)
    .replace(/[,:.-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  if (!normalized) {
    return true;
  }

  if (/\d/.test(normalized) || quantityUnitRegex.test(value) || reversedQuantityUnitRegex.test(value)) {
    return false;
  }

  return /\b(selam|ahbab|hvala|nadam se|ako moze|molim vas|dobar dan|pozdrav|vozac|nazove|nazvati|tu sam ja|kontakt)\b/.test(normalized);
}

function parseQuickQuantityOnly(value: string) {
  const normalized = value.replace(/\s+/g, "");

  if (!new RegExp(`^(?:${quickQuantityPattern})(?:\\+(?:${quickQuantityPattern}))*$`, "i").test(normalized)) {
    return null;
  }

  const parts = normalized.split("+");
  const quantity = parts.reduce((sum, part) => sum + parseQuickDecimal(part), 0);

  return quantity
    ? {
        note: parts.length > 1 ? `Original quantity shorthand: ${value}` : "Quantity-only repeat order",
        quantity,
      }
    : null;
}

function isLikelySupplierHeading(value: string) {
  const line = cleanQuickLine(value);

  if (!line.endsWith(":")) {
    return false;
  }

  const label = line.slice(0, -1).trim().toLowerCase();
  return Boolean(label) && !quantityUnitRegex.test(label) && !["napomena", "nap", "note", "komentar"].includes(label);
}

function categoryForQuickItem(name: string) {
  const normalized = name
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

  if (/\b(piletina|pileci|june|juneci|teletina|goved|meso|kobas|cevap|riba|losos|pastrmka|fasiran|fasirano|mljeven|mljeveno)\b/.test(normalized)) {
    return "Meat and fish";
  }

  if (/\b(paradajz|rajcica|luk|kupus\w*|krompir|salata|iceberg|krastav\w*|paprika|mrkva|persun|jabuka|banana|limun|povrce|voce)\b/.test(normalized)) {
    return "Produce";
  }

  if (/\b(mlijeko|mleko|sir|kajmak|vrhnje|jogurt|puter|maslac|pavlaka)\b/.test(normalized)) {
    return "Dairy";
  }

  if (/\b(hljeb|hleb|pecivo|brasno|kifla|somun)\b/.test(normalized)) {
    return "Bakery";
  }

  if (/\b(sok|voda|cola|kola|pivo|vino|rakija|kafa|caj)\b/.test(normalized)) {
    return "Beverages";
  }

  if (/(kutij|ambalaz|salvet|casa|čaša|poklop|folija|kesa|vreca|papir)/.test(normalized)) {
    return "Packaging";
  }

  return "Manual item";
}

export function parseQuickOrderText(
  value: string,
  options: { defaultSupplier?: string } = {},
): QuickOrderItemInput[] {
  let currentSupplier = options.defaultSupplier?.trim() || "";
  const parsedItems: QuickOrderItemInput[] = [];

  value.split(/\r?\n/).forEach((line) => {
    const cleanLine = cleanQuickLine(line);

    if (!cleanLine) {
      return;
    }

    if (isQuickChatterLine(cleanLine)) {
      return;
    }

    if (isLikelySupplierHeading(cleanLine)) {
      currentSupplier = cleanQuickSupplierName(cleanLine.slice(0, -1));
      return;
    }

    const parsed = parseQuickOrderLine(cleanLine, { defaultSupplier: currentSupplier });
    if (parsed) {
      parsedItems.push(parsed);
    }
  });

  return parsedItems;
}

export function parseQuickOrderLine(
  value: string,
  options: { defaultSupplier?: string } = {},
): QuickOrderItemInput | null {
  let working = cleanQuickLine(value);

  if (!working) {
    return null;
  }

  let supplier = options.defaultSupplier?.trim() || "";
  const supplierPrefix = working.match(/^([^:]{2,70}):\s+(.+)$/);
  if (supplierPrefix && !quantityUnitRegex.test(supplierPrefix[1]) && !/^(napomena|nap|note|komentar)$/i.test(supplierPrefix[1])) {
    supplier = cleanQuickSupplierName(supplierPrefix[1]);
    working = supplierPrefix[2].trim();
  }

  const supplierDashPrefix = working.match(/^([A-Za-z0-9 .&-]{2,70})\s[-–—]\s+(.+)$/);
  if (
    supplierDashPrefix &&
    !quantityUnitRegex.test(supplierDashPrefix[1]) &&
    (quantityUnitRegex.test(supplierDashPrefix[2]) ||
      reversedQuantityUnitRegex.test(supplierDashPrefix[2]) ||
      multiplierWithUnitRegex.test(supplierDashPrefix[2]))
  ) {
    supplier = cleanQuickSupplierName(supplierDashPrefix[1]);
    working = supplierDashPrefix[2].trim();
  }

  working = stripQuickOrderChatter(working);
  if (!working) {
    return null;
  }

  let note = "";
  const noteMatch = working.match(/\b(?:napomena|nap\.?|note|biljeska|bilje\u0161ka|komentar)\b\s*[:=.-]?\s*(.+)$/i);
  if (noteMatch) {
    note = noteMatch[1].replace(/[,\-|]+$/g, "").trim();
    working = stripTextRange(working, noteMatch);
  }

  const parentheticalNoteMatch = note ? null : working.match(/\(([^()]{2,90})\)\s*$/);
  if (parentheticalNoteMatch) {
    note = parentheticalNoteMatch[1].trim();
    working = stripTextRange(working, parentheticalNoteMatch);
  }

  let quantity = 1;
  let unit = "unit";
  const quantityMatch = working.match(quantityUnitRegex);

  if (quantityMatch) {
    quantity = parseQuickDecimal(quantityMatch[1]) || 1;
    unit = normalizeQuickUnit(quantityMatch[2]);
    working = stripTextRange(working, quantityMatch);
  } else {
    const reversedQuantityMatch = working.match(reversedQuantityUnitRegex);

    if (reversedQuantityMatch) {
      quantity = parseQuickDecimal(reversedQuantityMatch[2]) || 1;
      unit = normalizeQuickUnit(reversedQuantityMatch[1]);
      working = stripTextRange(working, reversedQuantityMatch);
    } else {
      const multiplierWithUnitMatch = working.match(multiplierWithUnitRegex);
      const multiplierMatch =
        multiplierWithUnitMatch ?? working.match(/(?:^|\s)(?:x\s*(\d+(?:[,.]\d+)?)|(\d+(?:[,.]\d+)?)\s*x)\b/i);

      if (multiplierMatch) {
        quantity = parseQuickDecimal(multiplierMatch[1] || multiplierMatch[2]) || 1;
        unit = multiplierWithUnitMatch ? normalizeQuickUnit(multiplierWithUnitMatch[3]) : "pcs";
        working = stripTextRange(working, multiplierMatch);

        if (!multiplierWithUnitMatch) {
          const leadingUnitMatch = working.match(leadingUnitAfterMultiplierRegex);
          if (leadingUnitMatch) {
            unit = normalizeQuickUnit(leadingUnitMatch[1]);
            working = leadingUnitMatch[2].trim();
          }
        }
      }
    }
  }

  const quantityOnlyMatch = quantity === 1 && unit === "unit" ? parseQuickQuantityOnly(working) : null;
  if (quantityOnlyMatch) {
    quantity = quantityOnlyMatch.quantity;
    note = note ? `${note}; ${quantityOnlyMatch.note}` : quantityOnlyMatch.note;
    working = "Usual item";
  }

  const portionMatch = working.match(/\bna\s+(\d+(?:[,.]\d+)?)\s*g\b/i);
  if (portionMatch) {
    const portionNote = `na ${portionMatch[1]}g`;
    note = note ? `${note}; ${portionNote}` : portionNote;
    working = stripTextRange(working, portionMatch);
  }

  let unitPrice = 0;
  const unitPriceMatch = working.match(unitPriceRegex);
  const explicitPriceMatch =
    unitPriceMatch ??
    working.match(/\b(?:at|po|price|cijena|cena)\s*(\d+(?:[,.]\d+)?)\s*(?:km|bam)?\b/i) ??
    working.match(/@\s*(\d+(?:[,.]\d+)?)\s*(?:km|bam)?\b/i);
  const trailingPriceMatch = explicitPriceMatch
    ? null
    : working.match(/\s(\d+(?:[,.]\d{1,2}))\s*(?:km|bam)?\s*$/i) ??
      working.match(/\s(\d+(?:[,.]\d+)?)\s*(?:km|bam)\s*$/i);
  const priceMatch = explicitPriceMatch ?? trailingPriceMatch;

  if (priceMatch) {
    unitPrice = parseQuickDecimal(priceMatch[1]);
    working = stripTextRange(working, priceMatch);
  }

  const supplierMatch = working.match(/\b(?:from|od|kod|supplier|dobavljac|dobavlja\u010d)\s+(.+)$/i);

  if (supplierMatch) {
    supplier = cleanQuickSupplierName(supplierMatch[1]);
    working = working.slice(0, supplierMatch.index).trim();
  } else {
    const supplierSuffix = working.match(/\s[-–—]\s*([A-ZČĆŠĐŽ][A-Za-zČĆŠĐŽčćšđž0-9 .&-]{1,70})$/);

    if (supplierSuffix && quantity !== 1) {
      supplier = cleanQuickSupplierName(supplierSuffix[1]);
      working = stripTextRange(working, supplierSuffix);
    }
  }

  const rawName = cleanQuickProductName(working.replace(/\b(?:za\s+sutra|sutra|danas)\b$/i, ""));
  const name = rawName || (quantity !== 1 || unit !== "unit" ? "Usual item" : "");
  if (!rawName && name) {
    note = note ? `${note}; Quantity-only repeat order` : "Quantity-only repeat order";
  }

  if (!name) {
    return null;
  }

  return {
    category: categoryForQuickItem(name),
    name,
    note: note || undefined,
    quantity,
    unit,
    supplier,
    unitPrice,
  };
}

export function parseQuickQuoteLine(value: string): QuickQuoteRequestInput | null {
  const parsedLine = parseQuickOrderLine(value);

  if (!parsedLine) {
    return null;
  }

  return {
    product: parsedLine.name,
    quantity: parsedLine.quantity,
    unit: parsedLine.unit,
    neededBy: "Tomorrow morning",
    supplierName: parsedLine.supplier,
  };
}

export function parseQuickInvoiceLine(value: string): QuickInvoiceInput | null {
  let working = value.trim().replace(/\s+/g, " ");

  if (!working) {
    return null;
  }

  const amountMatch = working.match(/\b(?:amount|total|iznos)?\s*(\d+(?:[,.]\d+)?)\s*(?:km|bam)\b/i);
  if (!amountMatch) {
    return null;
  }

  const amountBam = parseQuickDecimal(amountMatch[1]);
  working = `${working.slice(0, amountMatch.index)} ${working.slice((amountMatch.index ?? 0) + amountMatch[0].length)}`.trim();

  let orderId = "";
  const orderMatch = working.match(/\b(?:order|narudzba|racun|invoice|#)\s*#?([a-z0-9-]+)/i);
  if (orderMatch) {
    orderId = orderMatch[1].trim();
    working = `${working.slice(0, orderMatch.index)} ${working.slice((orderMatch.index ?? 0) + orderMatch[0].length)}`.trim();
  }

  let due = "Match today";
  const dueMatch = working.match(/\b(?:due|rok|pay|plati|dospijeva)\s+(.+)$/i);
  if (dueMatch) {
    due = dueMatch[1].replace(/[,\-|]+$/g, "").trim() || due;
    working = working.slice(0, dueMatch.index).trim();
  }

  const supplierName = working.replace(/[,\-|]+/g, " ").replace(/\s+/g, " ").trim() || "Supplier pending";

  if (!amountBam) {
    return null;
  }

  return {
    supplierName,
    amountBam,
    orderId,
    due,
  };
}

export function parseQuickInvoiceLineItems(value: string): QuickOrderItemInput[] {
  return parseQuickOrderText(value).map((item) => ({
    ...item,
    supplier: item.supplier || "",
  }));
}
